// frontend/src/features/seo/components/EditContentBriefModal.js
import React, { useState, useEffect } from 'react';

export default function EditContentBriefModal({ isOpen, onClose, brief, onSave }) {
  const [formData, setFormData] = useState(null);

  useEffect(() => {
    if (brief) {
      setFormData({
        title: brief.title || '',
        targetSlug: brief.targetSlug || '',
        searchIntent: brief.searchIntent || 'informational',
        targetWordCount: brief.targetWordCount || 2000,
        targetAudience: brief.targetAudience || '',
        conversionObjective: brief.conversionObjective || '',
        cta: brief.cta || '',
        outline: Array.isArray(brief.outline) ? brief.outline.map((item) => ({ ...item })) : [],
        proofPoints: Array.isArray(brief.proofPoints) ? [...brief.proofPoints] : [],
        internalLinks: Array.isArray(brief.internalLinks)
          ? brief.internalLinks.map((link) => ({ ...link }))
          : [],
      });
    }
  }, [brief]);

  if (!isOpen || !brief || !formData) return null;

  const isCurrentlyApproved = brief.status === 'approved';

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Outline item operations
  const handleOutlineChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.outline];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, outline: updated };
    });
  };

  const handleAddOutlineItem = () => {
    setFormData((prev) => ({
      ...prev,
      outline: [...prev.outline, { level: 'H2', text: '' }],
    }));
  };

  const handleRemoveOutlineItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      outline: prev.outline.filter((_, i) => i !== index),
    }));
  };

  const handleMoveOutlineItem = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= formData.outline.length) return;

    setFormData((prev) => {
      const updated = [...prev.outline];
      const temp = updated[index];
      updated[index] = updated[targetIndex];
      updated[targetIndex] = temp;
      return { ...prev, outline: updated };
    });
  };

  // Proof points operations
  const handleProofPointChange = (index, value) => {
    setFormData((prev) => {
      const updated = [...prev.proofPoints];
      updated[index] = value;
      return { ...prev, proofPoints: updated };
    });
  };

  const handleAddProofPoint = () => {
    setFormData((prev) => ({
      ...prev,
      proofPoints: [...prev.proofPoints, ''],
    }));
  };

  const handleRemoveProofPoint = (index) => {
    setFormData((prev) => ({
      ...prev,
      proofPoints: prev.proofPoints.filter((_, i) => i !== index),
    }));
  };

  // Internal links operations
  const handleInternalLinkChange = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.internalLinks];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, internalLinks: updated };
    });
  };

  const handleAddInternalLink = () => {
    setFormData((prev) => ({
      ...prev,
      internalLinks: [...prev.internalLinks, { page: '', anchorText: '' }],
    }));
  };

  const handleRemoveInternalLink = (index) => {
    setFormData((prev) => ({
      ...prev,
      internalLinks: prev.internalLinks.filter((_, i) => i !== index),
    }));
  };

  const handleClearAll = () => {
    setFormData({
      title: '',
      targetSlug: '',
      searchIntent: 'informational',
      targetWordCount: '',
      targetAudience: '',
      conversionObjective: '',
      cta: '',
      outline: [],
      proofPoints: [],
      internalLinks: [],
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Prepare updated brief object
    const updatedBrief = {
      ...brief,
      title: formData.title.trim(),
      targetSlug: formData.targetSlug.startsWith('/')
        ? formData.targetSlug.trim()
        : `/${formData.targetSlug.trim()}`,
      searchIntent: formData.searchIntent,
      targetWordCount: parseInt(formData.targetWordCount, 10) || 2000,
      targetAudience: formData.targetAudience.trim(),
      conversionObjective: formData.conversionObjective.trim(),
      cta: formData.cta.trim(),
      outline: formData.outline.filter((item) => item.text.trim().length > 0),
      proofPoints: formData.proofPoints.filter((pt) => pt.trim().length > 0),
      internalLinks: formData.internalLinks.filter((link) => link.page.trim().length > 0),
      // BR-05 & Repository Invariant §10: Material edits invalidate prior approval
      status: isCurrentlyApproved ? 'under_review' : brief.status,
      approvedBy: isCurrentlyApproved ? null : brief.approvedBy,
      approvedAt: isCurrentlyApproved ? null : brief.approvedAt,
    };

    onSave(updatedBrief);
    onClose();
  };

  return (
    <div className="seo-modal-overlay" onClick={onClose} data-testid="edit-brief-modal">
      <div className="seo-modal-dialog seo-modal-dialog-large" onClick={(e) => e.stopPropagation()}>
        <div className="seo-modal-header">
          <div>
            <div className="seo-spec-tag-row">
              <span className="seo-spec-tag subtle">Human Review</span>
            </div>
            <h3 className="seo-modal-title">
              <i className="fas fa-edit text-blue"></i> Edit Content Brief
            </h3>
          </div>
          <button
            type="button"
            className="seo-modal-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="seo-brief-edit-form">
          <div className="seo-modal-body seo-brief-edit-body">
            {isCurrentlyApproved && (
              <div className="seo-brief-invalidation-notice" data-testid="approval-warning">
                <i className="fas fa-triangle-exclamation"></i>
                <div>
                  <strong>Editorial Governance Rule (BR-05):</strong>
                  <span>
                    {' '}
                    This brief is currently approved. Saving changes will reset its status to{' '}
                    <em>Under Review</em> to require re-approval before drafting in Content Studio.
                  </span>
                </div>
              </div>
            )}

            {/* Section 1: Article Metadata */}
            <div className="seo-edit-section">
              <h4 className="seo-edit-section-title">
                <i className="fas fa-file-lines"></i> Core Metadata
              </h4>
              <div className="seo-grid-two-col">
                <div className="seo-field-group">
                  <label className="seo-field-label">Target Article Title</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    value={formData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    required
                  />
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Target URL Slug</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    value={formData.targetSlug}
                    onChange={(e) => handleInputChange('targetSlug', e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="seo-grid-two-col">
                <div className="seo-field-group">
                  <label className="seo-field-label">Search Intent</label>
                  <select
                    className="seo-select-input"
                    value={formData.searchIntent}
                    onChange={(e) => handleInputChange('searchIntent', e.target.value)}
                  >
                    <option value="informational">Informational</option>
                    <option value="commercial">Commercial</option>
                    <option value="transactional">Transactional</option>
                    <option value="navigational">Navigational</option>
                  </select>
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Target Word Count</label>
                  <input
                    type="number"
                    min="300"
                    max="10000"
                    step="100"
                    className="seo-text-input"
                    value={formData.targetWordCount}
                    onChange={(e) => handleInputChange('targetWordCount', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Audience & Conversion Goals */}
            <div className="seo-edit-section">
              <h4 className="seo-edit-section-title">
                <i className="fas fa-bullseye"></i> Strategy &amp; Conversion Objectives
              </h4>
              <div className="seo-field-group">
                <label className="seo-field-label">Target Audience / ICP</label>
                <input
                  type="text"
                  className="seo-text-input"
                  value={formData.targetAudience}
                  onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                  placeholder="e.g. VP of Sales, Revenue Operations, Founders"
                />
              </div>

              <div className="seo-grid-two-col">
                <div className="seo-field-group">
                  <label className="seo-field-label">Conversion Objective</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    value={formData.conversionObjective}
                    onChange={(e) => handleInputChange('conversionObjective', e.target.value)}
                    placeholder="e.g. Book Demo via Lead Huntrix"
                  />
                </div>

                <div className="seo-field-group">
                  <label className="seo-field-label">Primary Call to Action (CTA)</label>
                  <input
                    type="text"
                    className="seo-text-input"
                    value={formData.cta}
                    onChange={(e) => handleInputChange('cta', e.target.value)}
                    placeholder="e.g. Get started with verified B2B lead generation"
                  />
                </div>
              </div>
            </div>

            {/* Section 3: AIEO Outline Editor */}
            <div className="seo-edit-section">
              <div className="seo-edit-section-header">
                <h4 className="seo-edit-section-title">
                  <i className="fas fa-list-ol text-blue"></i> AIEO Structured Outline
                </h4>
                <button
                  type="button"
                  className="seo-btn seo-btn-sm seo-btn-secondary"
                  onClick={handleAddOutlineItem}
                >
                  <i className="fas fa-plus"></i> Add Heading
                </button>
              </div>

              <div className="seo-outline-edit-list">
                {formData.outline.map((item, idx) => (
                  <div key={idx} className="seo-outline-edit-row">
                    <select
                      className="seo-level-select"
                      value={item.level}
                      onChange={(e) => handleOutlineChange(idx, 'level', e.target.value)}
                    >
                      <option value="H1">H1</option>
                      <option value="H2">H2</option>
                      <option value="H3">H3</option>
                    </select>

                    <input
                      type="text"
                      className="seo-text-input seo-outline-input"
                      value={item.text}
                      onChange={(e) => handleOutlineChange(idx, 'text', e.target.value)}
                      placeholder="Heading text..."
                    />

                    <div className="seo-item-reorder-group">
                      <button
                        type="button"
                        className="seo-icon-btn"
                        title="Move Up"
                        disabled={idx === 0}
                        onClick={() => handleMoveOutlineItem(idx, -1)}
                      >
                        <i className="fas fa-arrow-up"></i>
                      </button>
                      <button
                        type="button"
                        className="seo-icon-btn"
                        title="Move Down"
                        disabled={idx === formData.outline.length - 1}
                        onClick={() => handleMoveOutlineItem(idx, 1)}
                      >
                        <i className="fas fa-arrow-down"></i>
                      </button>
                    </div>

                    <button
                      type="button"
                      className="seo-icon-btn text-danger"
                      title="Remove Heading"
                      onClick={() => handleRemoveOutlineItem(idx)}
                    >
                      <i className="fas fa-trash-can"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 4: Verified Proof Points */}
            <div className="seo-edit-section">
              <div className="seo-edit-section-header">
                <h4 className="seo-edit-section-title">
                  <i className="fas fa-quote-left text-green"></i> Verified Proof Points &amp;
                  Citations
                </h4>
                <button
                  type="button"
                  className="seo-btn seo-btn-sm seo-btn-secondary"
                  onClick={handleAddProofPoint}
                >
                  <i className="fas fa-plus"></i> Add Proof Point
                </button>
              </div>

              <div className="seo-simple-edit-list">
                {formData.proofPoints.map((point, idx) => (
                  <div key={idx} className="seo-simple-edit-row">
                    <input
                      type="text"
                      className="seo-text-input"
                      value={point}
                      onChange={(e) => handleProofPointChange(idx, e.target.value)}
                      placeholder="e.g. Verifiable statistical proof point or benchmark..."
                    />
                    <button
                      type="button"
                      className="seo-icon-btn text-danger"
                      title="Remove"
                      onClick={() => handleRemoveProofPoint(idx)}
                    >
                      <i className="fas fa-trash-can"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Section 5: Internal Links */}
            <div className="seo-edit-section">
              <div className="seo-edit-section-header">
                <h4 className="seo-edit-section-title">
                  <i className="fas fa-link text-purple"></i> Contextual Internal Links
                </h4>
                <button
                  type="button"
                  className="seo-btn seo-btn-sm seo-btn-secondary"
                  onClick={handleAddInternalLink}
                >
                  <i className="fas fa-plus"></i> Add Internal Link
                </button>
              </div>

              <div className="seo-links-edit-list">
                {formData.internalLinks.map((link, idx) => (
                  <div key={idx} className="seo-links-edit-row">
                    <input
                      type="text"
                      className="seo-text-input"
                      value={link.page}
                      onChange={(e) => handleInternalLinkChange(idx, 'page', e.target.value)}
                      placeholder="Target page path (e.g. /features/campaign)"
                    />
                    <input
                      type="text"
                      className="seo-text-input"
                      value={link.anchorText}
                      onChange={(e) => handleInternalLinkChange(idx, 'anchorText', e.target.value)}
                      placeholder="Anchor text"
                    />
                    <button
                      type="button"
                      className="seo-icon-btn text-danger"
                      title="Remove"
                      onClick={() => handleRemoveInternalLink(idx)}
                    >
                      <i className="fas fa-trash-can"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="seo-modal-footer">
            <button
              type="button"
              className="seo-btn seo-btn-clear"
              onClick={handleClearAll}
              title="Clear all input fields"
              data-testid="clear-all-inputs-btn"
            >
              <i className="fas fa-eraser"></i>
              <span>Clear All</span>
            </button>
            <div className="seo-modal-footer-right">
              <button type="button" className="seo-btn seo-btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="seo-btn seo-btn-primary">
                <i className="fas fa-save"></i>
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
